import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { 
  User, 
  createUserWithEmailAndPassword, 
  signInWithEmailAndPassword, 
  signOut, 
  onAuthStateChanged 
} from 'firebase/auth';
import { 
  doc, 
  setDoc, 
  getDoc, 
  updateDoc,
  collection,
  getDocs,
  query,
  orderBy,
  serverTimestamp 
} from 'firebase/firestore';
import { ref, remove } from 'firebase/database';
import { auth, db, realtimeDb } from '../config/firebase';

interface UserData {
  uid: string;
  email: string;
  username: string;
  displayName?: string;
  bio?: string;
  location?: string;
  website?: string;
  discord?: string;
  steam?: string;
  github?: string;
  role: 'admin' | 'user';
  isActive: boolean;
  createdAt: any;
  lastLogin: any;
  lastLoginIP: string;
  loginCount: number;
}

interface AuthContextType {
  currentUser: User | null;
  userData: UserData | null;
  loading: boolean;
  register: (email: string, password: string, username: string, userIP: string) => Promise<void>;
  login: (email: string, password: string, userIP: string) => Promise<void>;
  logout: () => Promise<void>;
  updateProfile: (profileData: Partial<UserData>) => Promise<void>;
  isAdmin: boolean;
  getAllUsers: () => Promise<UserData[]>;
  updateUserRole: (uid: string, role: 'admin' | 'user') => Promise<void>;
  toggleUserStatus: (uid: string, isActive: boolean) => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({} as AuthContextType);

export const useAuth = () => {
  return useContext(AuthContext);
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [userData, setUserData] = useState<UserData | null>(null);
  const [loading, setLoading] = useState(true);


  // Kullanıcı kaydı
  const register = async (email: string, password: string, username: string, userIP: string) => {
    try {
      // Firebase Authentication ile kullanıcı oluştur
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;

      // Firestore'da kullanıcı bilgilerini sakla
      const userData: UserData = {
        uid: user.uid,
        email: user.email || '',
        username: username,
        role: 'user',
        isActive: true,
        createdAt: serverTimestamp(),
        lastLogin: serverTimestamp(),
        lastLoginIP: userIP,
        loginCount: 1
      };

      await setDoc(doc(db, 'users', user.uid), userData);
      setUserData(userData);
      
    } catch (error: any) {
      // Hata yönetimi - loglanmaz güvenlik nedeniyle
      throw new Error(error.message || 'Kayıt işlemi sırasında bir hata oluştu');
    }
  };

  // Kullanıcı girişi
  const login = async (email: string, password: string, userIP: string) => {
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;

      // Kullanıcı bilgilerini kontrol et
      const userRef = doc(db, 'users', user.uid);
      const userSnap = await getDoc(userRef);
      
      if (userSnap.exists()) {
        const userData = userSnap.data();
        
        
        // Kullanıcı durumunu kontrol et
        if (userData.isActive === false) {
          // Kullanıcı pasif, çıkış yap
          await signOut(auth);
          throw new Error('Hesabınız deaktif edilmiştir. Lütfen yönetici ile iletişime geçin.');
        }
        
        const updatedData = {
          lastLogin: serverTimestamp(),
          lastLoginIP: userIP,
          loginCount: (userData.loginCount || 0) + 1
        };
        
        await setDoc(userRef, updatedData, { merge: true });
        
        // Güncellenmiş kullanıcı bilgilerini al
        const updatedSnap = await getDoc(userRef);
        setUserData({ ...updatedSnap.data(), uid: user.uid } as UserData);
      }
      
    } catch (error: any) {
      // Hata yönetimi - loglanmaz güvenlik nedeniyle
      throw new Error(error.message || 'Giriş yapılırken bir hata oluştu');
    }
  };

  // Kullanıcı çıkışı
  const logout = async () => {
    try {
      // Önce online status'u temizle
      if (currentUser) {
        const userStatusRef = ref(realtimeDb, `onlineUsers/${currentUser.uid}`);
        await remove(userStatusRef);
        
        // Ek güvenlik için bir kez daha temizle
        setTimeout(async () => {
          try {
            await remove(userStatusRef);
          } catch (e) {
            // Hata olursa görmezden gel
          }
        }, 1000);
      }
      
      await signOut(auth);
      setUserData(null);
    } catch (error: any) {
      console.error('Çıkış hatası:', error);
      throw new Error(error.message);
    }
  };

  // Profil güncelleme
  const updateProfile = async (profileData: Partial<UserData>) => {
    try {
      if (!currentUser) {
        throw new Error('Kullanıcı giriş yapmamış');
      }

      const userRef = doc(db, 'users', currentUser.uid);
      
      // Sadece güncellenebilir alanları güncelle
      const updateData = {
        username: profileData.username,
        displayName: profileData.displayName,
        bio: profileData.bio,
        location: profileData.location,
        website: profileData.website,
        discord: profileData.discord,
        steam: profileData.steam,
        github: profileData.github,
        updatedAt: serverTimestamp()
      };

      // Boş değerleri temizle
      Object.keys(updateData).forEach(key => {
        if (updateData[key as keyof typeof updateData] === '' || updateData[key as keyof typeof updateData] === undefined) {
          delete updateData[key as keyof typeof updateData];
        }
      });

      await updateDoc(userRef, updateData);
      
      // Güncellenmiş kullanıcı bilgilerini al
      const updatedSnap = await getDoc(userRef);
      if (updatedSnap.exists()) {
        setUserData({ ...updatedSnap.data(), uid: currentUser.uid } as UserData);
      }
      
    } catch (error: any) {
      console.error('Profil güncelleme hatası:', error);
      throw new Error(error.message);
    }
  };

  // Admin kontrolü
  const isAdmin = userData?.role === 'admin';

  // Tüm kullanıcıları getir (sadece admin)
  const getAllUsers = async (): Promise<UserData[]> => {
    try {
      if (!isAdmin) {
        throw new Error('Bu işlem için admin yetkisi gereklidir');
      }

      const usersRef = collection(db, 'users');
      const q = query(usersRef, orderBy('createdAt', 'desc'));
      const querySnapshot = await getDocs(q);
      
      const users: UserData[] = [];
      querySnapshot.forEach((doc) => {
        users.push({ ...doc.data(), uid: doc.id } as UserData);
      });
      
      return users;
    } catch (error: any) {
      console.error('Kullanıcılar getirilemedi:', error);
      throw new Error(error.message);
    }
  };

  // Kullanıcı rolünü güncelle (sadece admin)
  const updateUserRole = async (uid: string, role: 'admin' | 'user') => {
    try {
      if (!isAdmin) {
        throw new Error('Bu işlem için admin yetkisi gereklidir');
      }

      const userRef = doc(db, 'users', uid);
      await setDoc(userRef, {
        role: role,
        updatedAt: serverTimestamp()
      }, { merge: true });
      
    } catch (error: any) {
      console.error('Rol güncelleme hatası:', error);
      throw new Error(error.message);
    }
  };

  // Kullanıcı durumunu değiştir (sadece admin)
  const toggleUserStatus = async (uid: string, isActive: boolean) => {
    try {
      // Admin kontrolü - daha güvenilir
      if (!currentUser || !userData) {
        throw new Error('Kullanıcı bilgileri yüklenmedi');
      }

      if (userData.role !== 'admin') {
        throw new Error('Bu işlem için admin yetkisi gereklidir');
      }


      const userRef = doc(db, 'users', uid);
      
      // Önce mevcut veriyi kontrol et
      await getDoc(userRef);
      
      await setDoc(userRef, {
        isActive: isActive,
        updatedAt: serverTimestamp()
      }, { merge: true });
      
      // Güncelleme sonrası kontrol et
      await getDoc(userRef);
    } catch (error: any) {
      console.error('Durum güncelleme hatası:', error);
      
      // Daha açıklayıcı hata mesajları
      if (error.code === 'permission-denied') {
        throw new Error('Bu işlem için yeterli yetkiniz yok. Lütfen admin olarak giriş yapın.');
      } else if (error.code === 'not-found') {
        throw new Error('Kullanıcı bulunamadı.');
      } else {
        throw new Error(`Durum güncellenemedi: ${error.message}`);
      }
    }
  };

  // Authentication state değişikliklerini dinle
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        // Kullanıcı giriş yapmış, Firestore'dan bilgileri al
        try {
          const userRef = doc(db, 'users', user.uid);
          const userSnap = await getDoc(userRef);
          
          if (userSnap.exists()) {
            const userData = userSnap.data();
            
            // Kullanıcı durumunu kontrol et
            if (!userData.isActive) {
              // Kullanıcı pasif, çıkış yap
              await signOut(auth);
              setUserData(null);
              setCurrentUser(null);
              setLoading(false);
              return;
            }
            
            setUserData({ ...userData, uid: user.uid } as UserData);
          }
        } catch (error) {
          console.error('Kullanıcı bilgileri alınamadı:', error);
        }
      } else {
        // Kullanıcı çıkış yaptı, online status'u temizle
        setUserData(null);
        // Online status'u temizlemek için ChatContext'e güveniyoruz
        // Çünkü burada currentUser henüz null olmamış olabilir
      }
      
      setCurrentUser(user);
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const value = {
    currentUser,
    userData,
    loading,
    register,
    login,
    logout,
    updateProfile,
    isAdmin,
    getAllUsers,
    updateUserRole,
    toggleUserStatus
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};
